<template>
  <v-form ref="form" v-set-test-id="['BaseForm', testId]" @submit="onSubmit">
    <input
      type="submit"
      style="position: absolute; opacity: 1; width: 1px; height: 1px"
      tabindex="-1"
    />
    <slot name="default" />
  </v-form>
</template>

<script lang="ts" setup>
import type { BaseValidation } from '@vuelidate/core';
import { nextTick, ref } from 'vue';
import { VForm } from 'vuetify/components/VForm';
import type { ActionableComponentProps } from '@/common/components/ActionableComponent';
import { vSetTestId } from '@/common/components/ActionableComponent';

type Props = ActionableComponentProps & {
  validation?: BaseValidation;
  nativeSubmit?: boolean;
};
const props = defineProps<Props>();
type Emits = { (e: 'submit'): void };
const emit = defineEmits<Emits>();

const form = ref<typeof VForm>();

const showErrors = (): Promise<void> =>
  new Promise(resolve => {
    props.validation?.$touch();

    nextTick(() => {
      if (!form.value) {
        throw new Error('no form found');
      }
      const element = form.value.$el.querySelector('.text-error:first-of-type');
      element?.scrollIntoView();

      const input = element?.querySelector('input, textarea') as
        | HTMLInputElement
        | HTMLTextAreaElement
        | undefined;
      input?.select();

      resolve();
    });
  });

const onSubmit = async (e: Event): Promise<void> => {
  if (!props.validation) {
    if (!props.nativeSubmit) {
      e.preventDefault();
    }
    emit('submit');
    return;
  }

  if (props.validation.$pending) {
    e.preventDefault();
    return;
  }

  if (props.validation.$invalid) {
    e.preventDefault();
    await showErrors();
    return;
  }

  if (!props.nativeSubmit) {
    e.preventDefault();
  }
  emit('submit');
};

defineExpose({ showErrors });
</script>
